import {
  buildNewCommissionBonusPayload,
  buildNewCommissionPlanPayload,
  calculateExpectedBonus,
  calculateExpectedCommission,
  resolveEffectiveCommissionableTuitionFee,
} from "./commissionPlanUtils";

const MONEY_EPS = 0.009;

export function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function tuitionDiffers(a, b) {
  return Math.abs(roundMoney(a) - roundMoney(b)) > MONEY_EPS;
}

export function isPendingInstallmentRow(row) {
  if (String(row?.status || "").toLowerCase() === "paid") return false;
  if (row?.financeInvoiceId || row?.invoiceId) return false;
  const label = String(
    row?.commissionStatusLabel || row?.status || "pending",
  ).toLowerCase();
  return label === "pending";
}

export function sumInstallmentTuition(installments = []) {
  return roundMoney(
    (Array.isArray(installments) ? installments : []).reduce(
      (sum, row) => sum + (Number(row.tuitionAmount) || 0),
      0,
    ),
  );
}

export function sumInstallmentPaidTuition(installments = []) {
  return roundMoney(
    (Array.isArray(installments) ? installments : []).reduce(
      (sum, row) =>
        sum +
        (Number(row.studentPaidTuition ?? row.student_paid_tuition) || 0),
      0,
    ),
  );
}

export function resolveInstallmentUnpaidTuition(row = {}) {
  const tuition = Math.max(Number(row.tuitionAmount ?? row.tuition_amount) || 0, 0);
  const paid = Math.max(
    Number(row.studentPaidTuition ?? row.student_paid_tuition) || 0,
    0,
  );
  return Math.max(0, roundMoney(tuition - paid));
}

export function sumBonusInstallmentAmount(installments = []) {
  return roundMoney(
    (Array.isArray(installments) ? installments : []).reduce(
      (sum, row) => sum + (Number(row.bonusAmount) || 0),
      0,
    ),
  );
}

/**
 * Compare allocated installment amounts to a new budget.
 * increase → remaining to add; decrease → overage that must be cleared/rebuilt.
 */
export function analyzeBudgetAllocation(allocated, budget) {
  const allocatedAmt = roundMoney(allocated);
  const budgetAmt = roundMoney(budget);
  const remaining = roundMoney(Math.max(0, budgetAmt - allocatedAmt));
  const overBy = roundMoney(Math.max(0, allocatedAmt - budgetAmt));
  return {
    allocated: allocatedAmt,
    budget: budgetAmt,
    remaining,
    overBy,
    isUnder: remaining > MONEY_EPS,
    isOver: overBy > MONEY_EPS,
    isMatched: Math.abs(allocatedAmt - budgetAmt) <= MONEY_EPS,
  };
}

export function splitInstallmentsByLock(installments = []) {
  const rows = Array.isArray(installments) ? installments : [];
  const pending = rows.filter((row) => isPendingInstallmentRow(row));
  const locked = rows.filter((row) => !isPendingInstallmentRow(row));
  return { pending, locked };
}

export function buildSyncedCommissionPlanPayload({
  plan,
  defaults = {},
  latestTuition,
  params = {},
  student,
  provider,
  course,
  application,
  bonus = null,
}) {
  // latestTuition = latest commissionable tuition from inquiry
  const commissionableTuitionFee = roundMoney(latestTuition);
  const totalTuitionFee = roundMoney(
    plan.tuitionFee ?? defaults.tuitionFee ?? 0,
  );
  const commissionType = plan.commissionType || "percentage";
  const commissionRate = Number(plan.commissionRate) || 0;
  const commissionGstType = plan.commissionGstType || "none";
  const commissionableForCommission = resolveEffectiveCommissionableTuitionFee({
    plan,
    bonus,
    tuitionFee: totalTuitionFee,
    commissionableTuitionFee,
  });
  const preview = calculateExpectedCommission({
    tuitionFee: totalTuitionFee,
    commissionableTuitionFee: commissionableForCommission,
    commissionType,
    commissionRate,
    commissionGstType,
  });

  return buildNewCommissionPlanPayload({
    applicationId: params.appId || application?.id || plan.applicationId,
    applicationCourseId:
      defaults.applicationCourseId || plan.applicationCourseId,
    clientId: params.studentId || student?.id || plan.clientId,
    providerId: params.providerId || provider?.id || plan.providerId,
    courseId: params.courseId || course?.id || plan.courseId,
    levelId: defaults.levelId || course?.levelId || plan.levelId,
    location: plan.location || defaults.location || "offshore",
    tuitionFee: totalTuitionFee,
    commissionable_tuition_fee: commissionableTuitionFee,
    commissionType,
    commissionRate,
    commissionGstType,
    expectedCommission: preview.expectedCommission,
    currency: plan.currency || defaults.currency || "AUD",
    status: plan.status || "active",
    hasBonus: Boolean(plan.hasBonus ?? plan.has_bonus),
    bonusFromCommissionable: Boolean(
      plan.bonusFromCommissionable ?? plan.bonus_from_commissionable,
    ),
    remarks: plan.remarks || plan.note || "",
    note: plan.note || plan.remarks || "",
  });
}

export function buildSyncedBonusPayload({ bonus, latestTuition }) {
  if (!bonus?.id) return null;
  const commissionableTuitionFee = roundMoney(latestTuition);
  const bonusType = bonus.bonusType || "fixed";
  const bonusValue = Number(bonus.bonusValue) || 0;
  const gstType = bonus.gstType || "none";

  // Fixed bonus is not driven by tuition — keep expected as-is unless percentage
  const preview =
    bonusType === "percentage"
      ? calculateExpectedBonus({
          commissionableTuitionFee,
          tuitionFee: commissionableTuitionFee,
          bonusType,
          bonusValue,
          gstType,
        })
      : {
          expectedBonus: Number(bonus.expectedBonus) || bonusValue,
        };

  return {
    payload: buildNewCommissionBonusPayload({
      bonusName: bonus.bonusName,
      bonusType,
      bonusValue,
      gstType,
      expectedBonus: preview.expectedBonus,
      status: bonus.status || "active",
      remarks: bonus.remarks || "",
    }),
    expectedBonus: preview.expectedBonus,
    tuitionDriven: bonusType === "percentage",
  };
}
